import { useState } from 'react';
import { Link as LinkIcon } from '@phosphor-icons/react';
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from '~/components/ui/accordion';
import { useResearchCitations, type ResearchCitation } from '~/hooks/queries/use-research';
// FRONT-2036 fix round 1 (items 14 & 16): the hostname derivation and the
// http(s)-only href sanitizer are the one shared copy in
// `research-display.ts` — see that module's docblock. Used to be an inline
// try/catch duplicated here.
import { hostOf, safeSourceHref } from './research-display';

interface SourcesBlockProps {
  runId: number;
}

/**
 * FRONT-2036 fix round 2 (N9): the citation list, extracted out of
 * `SourcesBlock`'s JSX so it's directly renderable in a test without the
 * surrounding `Accordion`'s open/closed state or the `enabled: open`-gated
 * query — both make the real component's citation markup absent from a
 * static (no-DOM, no-click) render entirely, the same "Radix doesn't render
 * AccordionContent's children while collapsed" fact
 * `custom-tool-output-files-strip.test.tsx` already documents, and there is
 * no prop to force `SourcesBlock`'s internal `open` state from outside it.
 * Same behaviour as before this extraction — only where the JSX lives
 * changed.
 */
export function SourcesList({ citations }: { citations: ResearchCitation[] }) {
  return (
    <ul className="mt-2 space-y-2 pl-4">
      {citations.map((c) => {
        // FRONT-2036 fix round 1 (item 16, hardening): `c.url` is
        // MODEL-authored — a non-http(s) scheme must never reach an
        // `<a href>`. Render as plain text instead of dropping the
        // citation entirely.
        const safeHref = safeSourceHref(c.url);
        return (
          <li key={c.id} className="text-[length:var(--text-2xs)] leading-snug">
            {safeHref ? (
              <a
                href={safeHref}
                target="_blank"
                rel="noreferrer"
                className="text-primary hover:underline font-medium"
              >
                {c.title || c.url}
              </a>
            ) : (
              <span className="font-medium">{c.title || c.url}</span>
            )}
            <span className="ml-2 text-muted-foreground/70">{hostOf(c.url)}</span>
            {c.snippet && <div className="text-muted-foreground mt-0.5">{c.snippet}</div>}
          </li>
        );
      })}
    </ul>
  );
}

/**
 * Expandable "Sources" disclosure rendered under a Deep Research
 * briefing message. Fetches citations on first expand so collapsed
 * briefings don't fan out N queries on page reload. Lists each URL with
 * its title + snippet so the user can audit any claim in the briefing
 * back to a source.
 */
export function SourcesBlock({ runId }: SourcesBlockProps) {
  const [open, setOpen] = useState(false);
  const { data: citations, isLoading } = useResearchCitations(runId, {
    enabled: open,
  });

  const count = citations?.length ?? 0;

  return (
    <Accordion
      type="single"
      collapsible
      value={open ? 'sources' : ''}
      onValueChange={(v) => setOpen(v === 'sources')}
      className="mt-3 -mb-1 border-t border-border/60 pt-2"
    >
      <AccordionItem value="sources" className="border-b-0">
        <AccordionTrigger className="w-fit flex-none gap-1.5 py-0 text-[length:var(--text-2xs)] font-medium text-muted-foreground hover:text-foreground hover:no-underline">
          <LinkIcon size={11} weight="bold" />
          <span>Sources</span>
          {open && isLoading && <span>(loading…)</span>}
          {open && !isLoading && count > 0 && <span>({count})</span>}
          {!open && <span>— click to view URLs</span>}
        </AccordionTrigger>
        <AccordionContent className="pb-0">
          {!isLoading && count === 0 && (
            <div className="text-[length:var(--text-2xs)] text-muted-foreground/80 mt-2 pl-4">
              No citations recorded for this run.
            </div>
          )}
          {count > 0 && <SourcesList citations={citations!} />}
        </AccordionContent>
      </AccordionItem>
    </Accordion>
  );
}
